import { Box, Stack } from "@chakra-ui/react";
import Joi from "joi";
import Head from "next/head";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import { updateData } from "@ud/store/insertForm/insertSlice";
import StepButtons from "components/InsertOfferWrapper/components/StepButtons";
import { useOfferInsert } from "contexts/InsertOffer";
import useForm, { OnFormSubmitType } from "hooks/useForm";
import useRouter from "hooks/useRouter";
import { SectionTitle } from "../components/SectionTitle";
import IconPin from "components/Chakra/Icons/Pin";
import { useTracking } from "contexts/Tracking";
import VillageInput from "components/Chakra/VillageInput/VillageInput";
import StreetInput from "components/Chakra/StreetInput";
import LocationMap from "components/Chakra/LocationMap";
import { ILocationSuggestion } from "utils/geo";
import { useAppSelector } from "hooks/reduxHooks";

type ILocation = {
  buttonNextText?: string;
  buttonPrevText?: string;
};

type IFormValue = {
  village: ILocationSuggestion | null;
  street: ILocationSuggestion | null;
};

const location = Joi.object({ id: Joi.string(), label: Joi.string() }).custom(
  (value, helpers) => {
    if (!value) {
      return helpers.error("any.invalid");
    }

    return value;
  },
);

const formSchema = Joi.object({
  village: location.required(),
  street: location.required(),
});

const LocationForm: React.FC<ILocation> = ({
  buttonNextText,
  buttonPrevText,
}) => {
  const [isFocusToVillage, setIsFocusToVillage] = useState(false);

  const insertReduxData = useAppSelector((e) => e.insertForm.data);
  const { updateStep } = useOfferInsert();
  const dispatch = useDispatch();
  const { handleSubmit, setValue, errors, watch } = useForm<IFormValue>(
    formSchema,
    {
      keepDataOnSuccess: true,
      defaultValues: {
        village: insertReduxData.village ?? null,
        street: insertReduxData.street ?? null,
      },
    },
  );

  const chosenCity = watch("village");
  const chosenStreet = watch("street");

  const { navigateTo } = useRouter();
  const { trackEvent } = useTracking();
  const { nextStep, getFirstStep } = useOfferInsert();

  const checkIsBtnDisable = (): boolean => !chosenStreet || !chosenCity;

  const handleData: OnFormSubmitType<IFormValue> = ({ street, village }) => {
    dispatch(
      updateData({
        data: { street, village },
      }),
    );

    const step = nextStep();
    navigateTo(step.url, {
      id: step.query?.id,
      status: step.query?.status,
      paidId: step.query?.paidId,
    });
    trackEvent({
      "dl.eCat": "offer_create",
      "dl.eAct": "bttn.clicked",
      "dl.eLab": "3. krok – Lokalita",
    });
    updateStep("finished");
  };

  useEffect(() => {
    if (!insertReduxData.typeInsert) {
      const step = getFirstStep();
      navigateTo(step.url, {
        id: step.query?.id,
        status: step.query?.status,
        paidId: step.query?.paidId,
      });
    }
  }, []);

  useEffect(
    () =>
      trackEvent({
        "dl.eCat": "offer_create",
        "dl.eAct": "page.loaded",
        "dl.eLab": "3. krok – Lokalita",
      }),
    [],
  );

  return (
    <>
      <Head>
        <link
          href="https://unpkg.com/maplibre-gl@2.4.0/dist/maplibre-gl.css"
          rel="stylesheet"
        />
      </Head>
      <form
        onSubmit={handleSubmit(handleData)}
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          gap: 0,
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        <Box textAlign="left">
          <SectionTitle
            title="Lokalita"
            isRequired
            titlePaddingProps={{ pt: "0px", pb: "16px" }}
            icon={<IconPin boxSize="16px" fillWith="ud-grey-600" />}
          />
          <Stack
            direction="row"
            mb="20px"
            sx={{
              ">*": {
                flex: 1,
              },
            }}
          >
            <VillageInput
              name="village"
              withBorders
              // @ts-expect-error fix
              error={errors?.village}
              focusNextElement={{
                isFocus: isFocusToVillage,
                element: "street",
              }}
              isNewInsertForm
              value={chosenCity ?? undefined}
              onInputChange={(value) => {
                setIsFocusToVillage(true);
                setValue(
                  "village",
                  { id: "", label: value },
                  { shouldValidate: true },
                );
              }}
              onLocationSelect={(loc) => {
                setIsFocusToVillage(true);
                setValue("village", loc, { shouldValidate: true });
              }}
            />

            <StreetInput
              name="street"
              // @ts-expect-error fix
              error={errors.street}
              isNewInsertForm
              onInputChange={(value) => {
                setValue(
                  "street",
                  { id: "", label: value },
                  { shouldValidate: true },
                );
              }}
              village={chosenCity ?? undefined}
              value={chosenCity?.id ? chosenStreet ?? undefined : undefined}
              onLocationSelect={(loc) => {
                setValue("street", loc, { shouldValidate: true });
              }}
            />
          </Stack>
          {chosenStreet?.id && chosenCity?.id && (
            <LocationMap village={chosenCity} street={chosenStreet} />
          )}
        </Box>
        <StepButtons
          checkIsBtnDisable={checkIsBtnDisable}
          buttonPrevText={buttonPrevText}
          buttonNextText={buttonNextText}
          mt={[null, "24px"]}
        />
      </form>
    </>
  );
};

export default LocationForm;
